import {
  action,
  computed,
  makeObservable,
  observable,
  reaction,
  runInAction
} from 'mobx';

import { AppNotificationType } from 'shared/entities/appNotifications';
import {
  AddCustomEmojiSetErrorCode,
  AddCustomEmojiSetParamsServer,
  CUSTOM_EMOJI_REFRESH_DELAY_MS,
  CUSTOM_EMOJI_REFRESH_MAX_ATTEMPTS,
  CUSTOM_EMOJI_REFRESH_MAX_NO_PROGRESS_ATTEMPTS,
  CUSTOM_EMOJI_PREVIEWS_REQUEST_LIMIT,
  CUSTOM_EMOJI_SETS_PAGE_SIZE,
  CustomEmojiSetsAddBlockedReason,
  CustomEmojiSetsListErrorCode,
  CustomEmojiSetNameParamsServer,
  CustomEmojiSetsListParamsServer,
  CustomEmojiSetsListServer,
  CustomEmojiSetServer,
  CustomEmojiPreviewsParamsServer,
  CustomEmojiPreviewsServer,
  ICustomEmojiSet,
  mapRefreshCustomEmojiSetErrorCodeToMessage,
  normalizeCustomEmojiSet,
  RefreshCustomEmojiSetErrorCode,
  RemoveCustomEmojiSetErrorCode
} from 'shared/entities/customEmoji';
import { apiUrls } from 'shared/entities/domain';
import { LoadingStage } from 'shared/entities/meta';
import { ApiCallResponse } from 'shared/entities/network';
import { IRootStore } from 'shared/entities/store/rootStore';
import {
  AddCustomEmojiSetResult,
  ICustomEmojiSetsStore
} from 'shared/entities/store/customEmojiSetsStore';
import { LoadingStageModel } from 'shared/models/loadingStage';
import NewListModel from 'shared/models/NewListModel';
import { ComponentLoadingStore } from 'stores/componentLoadingStore';

const ADD_FORM_ERROR_CODES = Object.values(AddCustomEmojiSetErrorCode);

/**
 * Единственный владелец наборов custom emoji проекта: их показывают и поле ответа
 * оператора, и редактор блока сценария, поэтому стор живёт на root уровне, а
 * shared-компоненты окна выбора читают его как глобальный стор, не заводя своих
 * запросов. Загрузка ленивая: список нужен только при открытии вкладки «Кастомные».
 *
 * Стадию загрузки списка держит сам `NewListModel` (`loadingStage` +
 * `isInitialLoad`): дозагрузка следующей страницы — продолжение той же операции,
 * а не независимая, и `InfiniteScroll` ждёт ровно эту пару полей.
 *
 * Здесь же кэш картинок по идентификаторам: в истории чата и в блоке лежат
 * только ID и обычная замена, а набор к этому моменту могли удалить из проекта,
 * поэтому картинку ищем не в списке наборов, а в `custom_emoji/previews`.
 */
export default class CustomEmojiSetsStore
  extends ComponentLoadingStore
  implements ICustomEmojiSetsStore
{
  readonly rootStore: IRootStore;

  readonly sets: NewListModel<ICustomEmojiSet> =
    new NewListModel<ICustomEmojiSet>();

  readonly addingStage = new LoadingStageModel();
  readonly refreshingStage = new LoadingStageModel();
  readonly removingStage = new LoadingStageModel();

  canAdd = false;
  addBlockedReason: CustomEmojiSetsAddBlockedReason | null = null;
  refreshingSetName: string | null = null;
  removingSetName: string | null = null;

  private _abortController: AbortController | null = null;
  private _refreshDelayController: AbortController | null = null;
  private _refreshCycleId = 0;
  /** Растёт при сбросе контекста: ответы прошлого проекта уже неприменимы */
  private _contextVersion = 0;

  /** Картинки по `custom_emoji_id`; `null` — картинки у элемента нет */
  private readonly _previews = observable.map<string, string | null>();
  /** Уже запрошенные ID: повторно, в том числе после ошибки, не запрашиваем */
  private readonly _requestedPreviewIds = new Set<string>();
  /** ID, собранные до ближайшей отправки пачки */
  private _pendingPreviewIds = new Set<string>();

  constructor(rootStore: IRootStore) {
    super();

    this.rootStore = rootStore;

    // `load`, `reset`, `canBeLoaded`, `loaded` и `shouldBeSkipped` уже
    // аннотированы в ComponentLoadingStore — повторно их объявлять нельзя.
    makeObservable<CustomEmojiSetsStore, '_applyPage' | '_applySet'>(this, {
      canAdd: observable,
      addBlockedReason: observable,
      refreshingSetName: observable,
      removingSetName: observable,

      isAvailable: computed,
      hasLoadError: computed,
      isSubmitting: computed,

      requestPreviews: action.bound,
      loadMore: action.bound,
      add: action.bound,
      refresh: action.bound,
      remove: action.bound,
      _applyPage: action,
      _applySet: action
    });
  }

  get canBeLoaded(): boolean {
    return this.rootStore.initialized && this.rootStore.userStore.isAuthorized;
  }

  /** Право «любое из двух», как у эндпоинтов наборов */
  get shouldBeSkipped(): boolean {
    const { canEditConversations, canEditProject } =
      this.rootStore.permissionsStore;

    return !canEditConversations && !canEditProject;
  }

  get isAvailable(): boolean {
    return !this.shouldBeSkipped;
  }

  get loaded(): boolean {
    return this.sets.loadingStage.successfullyLoaded;
  }

  get hasLoadError(): boolean {
    return this.sets.loadingStage.value === LoadingStage.ERROR;
  }

  get isSubmitting(): boolean {
    return (
      this.addingStage.isLoading ||
      this.refreshingStage.isLoading ||
      this.removingStage.isLoading
    );
  }

  /*
   * Стрелочное свойство: картинку по идентификатору читает рендер истории,
   * получая метод отдельно от стора, — обычный метод потерял бы `this`.
   */
  getPreviewUrl = (customEmojiId: string): string | null => {
    return this._previews.get(customEmojiId) ?? null;
  };

  requestPreviews(customEmojiIds: string[]): void {
    const unknownIds = customEmojiIds.filter(
      (id) => !this._requestedPreviewIds.has(id)
    );

    if (unknownIds.length === 0) {
      return;
    }

    unknownIds.forEach((id) => {
      this._requestedPreviewIds.add(id);
      this._pendingPreviewIds.add(id);
    });

    // Сообщения ленты монтируются по одному: пачку собираем до конца текущей
    // задачи, иначе на каждое сообщение ушёл бы свой запрос.
    void Promise.resolve().then(() => this._flushPendingPreviews());
  }

  initReactions(): void {
    super.initReactions();

    this.addReaction({
      key: 'projectId',
      reaction: reaction(
        () => this.rootStore.projectId,
        () => {
          this.reset();
        }
      )
    });
  }

  async load(): Promise<BaseResponse> {
    return this._loadPage(0);
  }

  async loadMore(): Promise<BaseResponse> {
    if (!this.sets.hasMore) {
      return { isError: false };
    }

    return this._loadPage(this.sets.length);
  }

  async add(url: string): Promise<AddCustomEmojiSetResult> {
    if (this.isSubmitting) {
      return { isError: true, data: { errorCode: null } };
    }

    const contextVersion = this._contextVersion;
    const data: AddCustomEmojiSetParamsServer = { url };

    this.addingStage.loading();

    const response = await this.rootStore.networkStore.api<
      CustomEmojiSetServer,
      AddCustomEmojiSetErrorCode
    >(apiUrls.CUSTOM_EMOJI_SETS_ADD, {
      method: 'POST',
      data,
      // Каждый код добавления — своё сообщение в форме рядом с полем ссылки,
      // тост его только продублировал бы.
      expectedErrorCodes: ADD_FORM_ERROR_CODES
    });

    // POST не отменяется, но ответ, догнавший смену проекта, к новому
    // контексту уже не относится.
    if (contextVersion !== this._contextVersion) {
      return { isError: true, data: { errorCode: null } };
    }

    if (response.isError) {
      const errorCode = response.data?.code ?? null;

      this.addingStage.error();

      // Лимит и отсутствие Telegram прилетают только на гонке с другим
      // менеджером: список уже не тот, что видит форма.
      if (
        errorCode === AddCustomEmojiSetErrorCode.limitReached ||
        errorCode === AddCustomEmojiSetErrorCode.telegramChannelRequired ||
        errorCode === AddCustomEmojiSetErrorCode.alreadyAdded
      ) {
        void this.load();
      }

      return { isError: true, data: { errorCode } };
    }

    await this.load();

    if (contextVersion !== this._contextVersion) {
      return { isError: true, data: { errorCode: null } };
    }

    this.addingStage.success();

    if (response.data.emojis.some((emoji) => emoji.preview_url === null)) {
      this._startRefreshCycle(response.data, contextVersion);
    }

    return { isError: false };
  }

  async refresh(setName: string): Promise<BaseResponse> {
    if (this.isSubmitting) {
      return { isError: true };
    }

    const contextVersion = this._contextVersion;
    const data: CustomEmojiSetNameParamsServer = { set_name: setName };

    const cycleId = this._beginRefreshCycle(setName);
    const response = await this._requestRefresh(data);

    if (contextVersion !== this._contextVersion) {
      return { isError: true };
    }

    if (response.isError) {
      await this.load();

      this._finishRefreshWithError(contextVersion, cycleId, setName);

      return { isError: true };
    }

    const isError = await this._runRefreshCycle(
      response.data,
      contextVersion,
      cycleId,
      1
    );

    return { isError };
  }

  async remove(setName: string): Promise<BaseResponse> {
    if (this.isSubmitting) {
      return { isError: true };
    }

    const contextVersion = this._contextVersion;
    const data: CustomEmojiSetNameParamsServer = { set_name: setName };

    this.removingStage.loading();
    this.removingSetName = setName;

    const response = await this.rootStore.networkStore.api<
      {},
      RemoveCustomEmojiSetErrorCode
    >(apiUrls.CUSTOM_EMOJI_SETS_REMOVE, {
      method: 'POST',
      data,
      // Набор мог удалить другой менеджер — для пользователя это тот же успех.
      expectedErrorCodes: [RemoveCustomEmojiSetErrorCode.notAdded]
    });

    if (contextVersion !== this._contextVersion) {
      return { isError: true };
    }

    const alreadyRemoved =
      response.isError &&
      response.data?.code === RemoveCustomEmojiSetErrorCode.notAdded;

    runInAction(() => {
      this.removingSetName = null;

      if (response.isError && !alreadyRemoved) {
        this.removingStage.error();
        return;
      }

      this.sets.removeEntity(setName);
      this.removingStage.success();
    });

    if (response.isError && !alreadyRemoved) {
      return { isError: true };
    }

    await this.load();

    return { isError: false };
  }

  reset(): void {
    this._abort();
    this._cancelRefreshDelay();
    this._contextVersion += 1;
    this._refreshCycleId += 1;
    this.sets.reset();
    this._previews.clear();
    this._requestedPreviewIds.clear();
    this._pendingPreviewIds = new Set<string>();
    this.canAdd = false;
    this.addBlockedReason = null;
    this.refreshingSetName = null;
    this.removingSetName = null;
    this.addingStage.reset();
    this.refreshingStage.reset();
    this.removingStage.reset();
  }

  private async _flushPendingPreviews(): Promise<void> {
    if (this._pendingPreviewIds.size === 0) {
      return;
    }

    const ids = Array.from(this._pendingPreviewIds);

    this._pendingPreviewIds = new Set<string>();

    for (
      let offset = 0;
      offset < ids.length;
      offset += CUSTOM_EMOJI_PREVIEWS_REQUEST_LIMIT
    ) {
      await this._loadPreviews(
        ids.slice(offset, offset + CUSTOM_EMOJI_PREVIEWS_REQUEST_LIMIT)
      );
    }
  }

  private async _loadPreviews(customEmojiIds: string[]): Promise<void> {
    const data: CustomEmojiPreviewsParamsServer = {
      custom_emoji_ids: customEmojiIds
    };

    const response =
      await this.rootStore.networkStore.api<CustomEmojiPreviewsServer>(
        apiUrls.CUSTOM_EMOJI_PREVIEWS,
        {
          method: 'POST',
          data,
          // Вместо картинки показывается обычная замена: тост о ней не нужен
          showUnexpectedError: false
        }
      );

    if (response.isError) {
      return;
    }

    runInAction(() => {
      response.data.items.forEach((item) => {
        this._previews.set(item.custom_emoji_id, item.preview_url);
      });
    });
  }

  private _startRefreshCycle(
    initialSet: CustomEmojiSetServer,
    contextVersion: number
  ): void {
    if (
      !initialSet.emojis.some((emoji) => emoji.preview_url === null) ||
      this.isSubmitting
    ) {
      return;
    }

    const cycleId = this._beginRefreshCycle(initialSet.set_name);

    void this._runRefreshCycle(initialSet, contextVersion, cycleId, 0);
  }

  private _beginRefreshCycle(setName: string): number {
    this._cancelRefreshDelay();

    const cycleId = ++this._refreshCycleId;

    runInAction(() => {
      this.refreshingStage.loading();
      this.refreshingSetName = setName;
    });

    this._refreshDelayController = new AbortController();

    return cycleId;
  }

  private async _runRefreshCycle(
    initialSet: CustomEmojiSetServer,
    contextVersion: number,
    cycleId: number,
    attempts: number
  ): Promise<boolean> {
    let previousMissingPreviews = this._applySet(initialSet);
    let noProgressAttempts = 0;

    try {
      if (
        !this._isCurrentRefresh(contextVersion, cycleId, initialSet.set_name)
      ) {
        return true;
      }

      if (previousMissingPreviews === 0) {
        this._finishRefreshSuccessfully(
          contextVersion,
          cycleId,
          initialSet.set_name
        );
        return false;
      }

      while (attempts < CUSTOM_EMOJI_REFRESH_MAX_ATTEMPTS) {
        const shouldContinue = await this._waitForRefreshDelay();

        if (
          !shouldContinue ||
          !this._isCurrentRefresh(contextVersion, cycleId, initialSet.set_name)
        ) {
          return true;
        }

        const response = await this._requestRefresh({
          set_name: initialSet.set_name
        });

        if (
          !this._isCurrentRefresh(contextVersion, cycleId, initialSet.set_name)
        ) {
          return true;
        }

        if (response.isError) {
          await this.load();

          this._finishRefreshWithError(
            contextVersion,
            cycleId,
            initialSet.set_name
          );
          return true;
        }

        attempts += 1;
        const missingPreviews = this._applySet(response.data);

        if (missingPreviews === 0) {
          this._finishRefreshSuccessfully(
            contextVersion,
            cycleId,
            initialSet.set_name
          );
          return false;
        }

        if (missingPreviews >= previousMissingPreviews) {
          noProgressAttempts += 1;
        } else {
          noProgressAttempts = 0;
        }

        previousMissingPreviews = missingPreviews;

        if (
          noProgressAttempts >= CUSTOM_EMOJI_REFRESH_MAX_NO_PROGRESS_ATTEMPTS ||
          attempts >= CUSTOM_EMOJI_REFRESH_MAX_ATTEMPTS
        ) {
          this._finishRefreshSuccessfully(
            contextVersion,
            cycleId,
            initialSet.set_name
          );
          return false;
        }
      }

      return false;
    } finally {
      if (this._refreshCycleId === cycleId) {
        this._refreshDelayController = null;
      }
    }
  }

  private _requestRefresh(
    data: CustomEmojiSetNameParamsServer
  ): Promise<
    ApiCallResponse<CustomEmojiSetServer, RefreshCustomEmojiSetErrorCode>
  > {
    return this.rootStore.networkStore.api<
      CustomEmojiSetServer,
      RefreshCustomEmojiSetErrorCode
    >(apiUrls.CUSTOM_EMOJI_SETS_REFRESH, {
      method: 'POST',
      data,
      errorsMap: mapRefreshCustomEmojiSetErrorCodeToMessage,
      expectedErrorCodes: [RefreshCustomEmojiSetErrorCode.notAdded]
    });
  }

  private _applySet(raw: CustomEmojiSetServer): number {
    const entity = normalizeCustomEmojiSet(raw);

    if (this.sets.getEntity(entity.setName)) {
      this.sets.updateEntity(entity.setName, entity);
    }

    this._cachePreviewsOfPage([raw]);

    return entity.emojis.filter((emoji) => emoji.previewUrl === null).length;
  }

  private _finishRefreshSuccessfully(
    contextVersion: number,
    cycleId: number,
    setName: string
  ): void {
    if (!this._isCurrentRefresh(contextVersion, cycleId, setName)) {
      return;
    }

    runInAction(() => {
      this.refreshingSetName = null;
      this.refreshingStage.success();
    });

    this.rootStore.appNotificationsStore.open({
      title: (t) =>
        t('CustomEmojiSetsStore.notifications.refreshSuccess', {
          ns: 'stores'
        }),
      type: AppNotificationType.success
    });
  }

  private _finishRefreshWithError(
    contextVersion: number,
    cycleId: number,
    setName: string
  ): void {
    if (!this._isCurrentRefresh(contextVersion, cycleId, setName)) {
      return;
    }

    runInAction(() => {
      this.refreshingSetName = null;
      this.refreshingStage.error();
    });
  }

  private _isCurrentRefresh(
    contextVersion: number,
    cycleId: number,
    setName: string
  ): boolean {
    return (
      contextVersion === this._contextVersion &&
      cycleId === this._refreshCycleId &&
      this.refreshingSetName === setName
    );
  }

  private _waitForRefreshDelay(): Promise<boolean> {
    const controller = this._refreshDelayController;

    if (!controller || controller.signal.aborted) {
      return Promise.resolve(false);
    }

    return new Promise((resolve) => {
      const onAbort = (): void => {
        clearTimeout(timer);
        resolve(false);
      };

      const timer = setTimeout(() => {
        controller.signal.removeEventListener('abort', onAbort);
        resolve(true);
      }, CUSTOM_EMOJI_REFRESH_DELAY_MS);

      controller.signal.addEventListener('abort', onAbort, { once: true });
    });
  }

  private async _loadPage(offset: number): Promise<BaseResponse> {
    if (
      !this.canBeLoaded ||
      this.shouldBeSkipped ||
      this.sets.loadingStage.isLoading
    ) {
      return { isError: true };
    }

    const controller = this._startRequest();
    const params: CustomEmojiSetsListParamsServer = {
      limit: CUSTOM_EMOJI_SETS_PAGE_SIZE,
      offset
    };

    this.sets.loadingStage.loading();

    const response = await this.rootStore.networkStore.api<
      CustomEmojiSetsListServer,
      CustomEmojiSetsListErrorCode
    >(apiUrls.CUSTOM_EMOJI_SETS_LIST, {
      method: 'GET',
      data: params,
      config: {
        signal: controller.signal
      },
      // Вкладка показывает свою ошибку с действием «Повторить»: тост и общий
      // экран ошибки её бы только продублировали.
      showUnexpectedError: false,
      expectedErrorCodes: [CustomEmojiSetsListErrorCode.listFailed]
    });

    // Устаревший ответ не трогает ни список, ни стадию: отменённый запрос
    // прошлого проекта не должен уводить вкладку в ошибку.
    if (this._abortController !== controller) {
      return { isError: true };
    }

    this._abortController = null;

    if (response.isError) {
      this.sets.loadingStage.error();

      return { isError: true };
    }

    this._applyPage(response.data, offset);

    return { isError: false };
  }

  private _applyPage(page: CustomEmojiSetsListServer, offset: number): void {
    this.sets.fillByRawData({
      raw: page.items,
      normalizer: (raw) => {
        const entity = normalizeCustomEmojiSet(raw);

        return { entity, key: entity.setName };
      },
      initial: offset === 0
    });
    this.sets.hasMoreModel.changeValue(page.pagination.has_more);
    this.sets.isInitialLoadModel.changeValue(false);
    this.canAdd = page.can_add;
    this.addBlockedReason = page.add_blocked_reason;
    this._cachePreviewsOfPage(page.items);
    this.sets.loadingStage.success();
  }

  /**
   * Картинки загруженных наборов — тот же ответ, что отдают `previews`, только
   * раньше. Без этого отправленный custom emoji сначала показывался обычной
   * заменой и менялся на картинку лишь после отдельного запроса.
   */
  private _cachePreviewsOfPage(items: CustomEmojiSetServer[]): void {
    items.forEach((item) => {
      item.emojis.forEach((emoji) => {
        if (emoji.preview_url === null) {
          return;
        }

        this._previews.set(emoji.custom_emoji_id, emoji.preview_url);
        this._requestedPreviewIds.add(emoji.custom_emoji_id);
      });
    });
  }

  private _startRequest(): AbortController {
    this._abort();

    const controller = new AbortController();
    this._abortController = controller;

    return controller;
  }

  private _abort(): void {
    this._abortController?.abort();
    this._abortController = null;
  }

  private _cancelRefreshDelay(): void {
    this._refreshDelayController?.abort();
    this._refreshDelayController = null;
  }
}
